import React, { useCallback, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import { getPlural } from 'lib/string/pluralize'
import { capitalise } from 'lib/string/stringUtils'
import MobileAndroidIcon from 'components/Luxkit/Icons/line/LineMobileAndroidIcon'
import ShieldCheckIcon from 'components/Luxkit/Icons/line/LineShieldCheckIcon'
import CalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import ChatIcon from 'components/Luxkit/Icons/line/LineChatIcon'
import ClockFiveIcon from 'components/Luxkit/Icons/line/LineClockFiveIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { rem } from 'polished'
import OfferBNPLAd from 'components/OfferPage/OfferBNPLAd'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import Pane from 'components/Common/Pane'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import ExperiencePriceDetails from '../ExperiencePriceDetails'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import Tooltip from 'components/Luxkit/Tooltip'
import TextLink from 'components/Luxkit/TextLink'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import PriceRowLuxPlusPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusNonMemberPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import Caption from 'components/Luxkit/Typography/Caption'
import { IsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { mediaQueryUp } from 'components/utils/breakpoint'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'

const HR = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`
const SoldOutPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.seven};

  > * + * {
    margin-top: ${rem(16)};
  }
`

const OverviewContainer = styled(VerticalSpacer)`
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(24)};
`

const TightContainer = styled.div`
  ${mediaQueryUp.tablet} {
    width: 120px;
  }
`
interface Props {
  experience: App.ExperienceOffer;
  onGiveAsGift?: () => void;
  luxPlusEnabled: boolean;
}

function ExperienceOfferPageOverview(props: Props) {
  const { experience, onGiveAsGift, luxPlusEnabled } = props
  const previewMode = useAppSelector(state => state.system.previewMode)
  const durationString = useMemo(() => getExperienceDurationString(experience), [experience])
  const isAvailable = experience.status === 'ONLINE' || previewMode

  // complimentary offers have zero price, we should block the booking through the standalone flow without showing the sold out messages
  const hasPrice = experience.price > 0

  const [soldOutSearchUrl] = useExperienceSearchUrl({ latLong: experience.location })
  const experiencesBNBLEnabled = useMemo(() => experience.allowBuyNowBookLater, [experience.allowBuyNowBookLater])
  const isVoucherBased = !experience.hasCalendar
  const trackEvent = useTrackExperienceOfferEvent()
  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const showLuxPlusPrice = useMemo(() => {
    return luxPlusEnabled && experience.pricing.memberPrice > 0
  }, [luxPlusEnabled, experience.pricing.memberPrice])

  const handleOnGiveAsGift = useCallback(() => {
    trackEvent('GIVE_AS_GIFT_CLICKED')
    onGiveAsGift?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [onGiveAsGift])

  const handleCheckAvailability = useCallback(() => {
    trackEvent('CHECK_AVAILABILITY_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleBNBLTooltipOpen = useCallback((isOpen: boolean) => {
    if (isOpen) trackEvent('BNBL_LEARN_MORE_BUTTON_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleBrowseAllExperiencesClick = useCallback(() => {
    trackEvent('BROWSE_ALL_EXPERIENCES_CLICK')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleInterestFreeLearnMoreClick = useCallback(() => {
    trackEvent('INTEREST_FREE_INFO_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleOnSupportEmailClick = useCallback(() => {
    trackEvent('SUPPORT_TEAM_EMAIL_BUTTON_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])
  const handleOnSupportNumberClick = useCallback(() => {
    trackEvent('SUPPORT_TEAM_NUMBER_BUTTON_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(experience, {
      ...(showLuxPlusPrice && {
        memberPrice: experience.pricing.memberPrice,
      }),
    }),
  ], [experience, showLuxPlusPrice])

  const shouldTrackPrice = isAvailable && hasPrice && !!experience?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && experience?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${experience.id}`,
        action: experience.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, experience?.price, experience?.id])

  return <VerticalSpacer gap={16}>
    <OverviewContainer gap={16}>
      <Heading variant="heading5">Overview</Heading>
      <VerticalSpacer gap={8}>
        {experience.copy.availability && <BodyTextBlock variant="medium" startIcon={<CalendarIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">Availability: </BodyText> {experience.copy.availability}
          </BodyText>
        </BodyTextBlock>}

        <BodyTextBlock variant="medium" startIcon={<ClockFiveIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">Duration:</BodyText> {durationString}
          </BodyText>
        </BodyTextBlock>

        {experience.voucher.typesAccepted.length > 0 && <BodyTextBlock variant="medium" startIcon={<MobileAndroidIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            {experience.voucher.typesAccepted.map(type => capitalise(type)).join(', ')}{' '}
            {getPlural('voucher', experience.voucher.typesAccepted.length)} accepted
          </BodyText>
        </BodyTextBlock>
        }

        {experience.copy.safetyInformation.length > 0 && <BodyTextBlock variant="medium" weight="bold" startIcon={<ShieldCheckIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            Safety measures applied
          </BodyText>
        </BodyTextBlock>
        }

        {experience.languages.length > 0 && <BodyTextBlock variant="medium" weight="bold" startIcon={<ChatIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">Language:</BodyText> {experience.languages.map(lang => lang.name).join(', ')}
          </BodyText>
        </BodyTextBlock>
        }

        {(isAvailable && hasPrice && experience.cancellationPolicies.length > 0 || experience.giftType) &&
          <Group direction="vertical" gap={8}>
            {experience.cancellationPolicies.length > 0 &&
              <ExperienceCancellationText
                cancellationInfo={experience.copy.cancellationInfo}
                onSupportEmailClick={handleOnSupportEmailClick}
                onSupportNumberClick={handleOnSupportNumberClick}
              />
            }
            {experience.giftType &&
              <ExperienceGiveAsGiftInfoText
                experience={experience}
                onSupportEmailClick={handleOnSupportEmailClick}
                onSupportNumberClick={handleOnSupportNumberClick}
              />
            }
          </Group>}
      </VerticalSpacer>

      {isAvailable && hasPrice && <>
        <HR />
        <VerticalSpacer gap={8}>
          <ExperienceAppDiscountTag experienceOffer={experience} />
          <div>

            {!showLuxPlusPrice && <PriceRowPriceWithCaption
              size="L"
              caption="From"
              price={experience.price}
              saleUnit={experience.ticketUnitLabel ? experience.ticketUnitLabel : 'total'}
            />}

            {/* LuxPlus Price - if available */}
            {!!showLuxPlusPrice && <>
              <Caption
                as="span"
                colour="neutral-two"
                weight="normal"
                format="propercase"
                variant="large"
              >
                from
              </Caption>
              <TightContainer>
                <PriceRowLuxPlusPrice
                  logoPlacement="trailing"
                  size="L"
                  memberPrice={experience.pricing.memberPrice}
                  saleUnit={experience.ticketUnitLabel ? experience.ticketUnitLabel : 'total'}
                />
              </TightContainer>
              <PriceRowLuxPlusNonMemberPrice
                size="L"
                nonMemberPrice={experience.price}
              />
            </>}

            {/* End LuxPlus Price - if available */}

            <PriceRowAgentHubCommission
              offerId={experience.id}
              productType={productType}
              offerLocations={[experience.location.name]}
              offer={experience}
              size="L"
            />
            {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={Math.ceil(experience.grossPrice)}
              discountPercentage={experience.discount}
            />}
            <ExperiencePriceDetails experience={experience} />
          </div>
        </VerticalSpacer>
        <LoyaltyPoints
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          hideStatusCredits
        />
        <TextButton kind="primary" href="#ticket-options" fit="full-width" onClick={handleCheckAvailability}>Check availability</TextButton>

        {experiencesBNBLEnabled && experience.giftType &&
          <TextButton kind="secondary" fit="full-width" onClick={handleOnGiveAsGift}>Give as gift</TextButton>
        }

        {experiencesBNBLEnabled && !isVoucherBased && experience.productType !== 'transfer' && <BodyTextBlock
          variant="small"
          weight="bold"
          startIcon={<ClockFiveIcon />}
        >
          Buy now, choose dates later available{' '}
          <Tooltip
            description="Secure an experience and have the flexibility to choose dates at a later time."
            onOpenChange={handleBNBLTooltipOpen}
          >
            <TextLink>Learn more</TextLink>
          </Tooltip>
        </BodyTextBlock>}
      </>}

      {!isAvailable && <>
        <HR />
        <SoldOutPane>
          <Heading variant="heading6" align="center">This offer has SOLD OUT</Heading>
          <TextButton kind="primary" fit="full-width" to={soldOutSearchUrl} onClick={handleBrowseAllExperiencesClick}>
            Browse all experiences
          </TextButton>
        </SoldOutPane>
      </>}

    </OverviewContainer>
    <OfferBNPLAd prices={experience.price} onLearnMore={handleInterestFreeLearnMoreClick} />
  </VerticalSpacer>
}

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: IsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(React.memo(ExperienceOfferPageOverview))
